import React, { useEffect, useState } from "react";
import {
  Button,
  Drawer,
  Form,
  Input,
  Table,
  Typography,
  Space,
  Tag,
  Empty,
  message,
  Card,
  Spin,
} from "antd";
import {
  PlusOutlined,
  LinkOutlined,
  AppstoreOutlined,
  DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { notify } from "../../customs/NotifyProvider";

const { Text } = Typography;

const API_URL = "https://cabbookigapi.shreeshtech.com/api/v1/seo/menu";

const delete_api ="https://cabbookigapi.shreeshtech.com/api/v1/delete"

const TopMenu = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const [form] = Form.useForm();

  // Fetch Menu
  const fetchMenu = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API_URL);
      setMenuItems(res.data.data || []);
    } catch (error) {
      message.error("Failed to load menu items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMenu();
  }, []);

  // Open Edit
  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue({
      menuName: record.menu_list,
      navigateTo: record.navigateTo,
    });
    setDrawerOpen(true);
  };

  // Save / Update
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload = {
        menu_list: values.menuName,
        navigateTo: values.navigateTo,
        added_on: new Date().toLocaleString(),
      };

      if (editingItem) {
        // UPDATE
        await axios.put(`${API_URL}/${editingItem.id}`, payload);

        setMenuItems((prev) =>
          prev.map((item) =>
            item.id === editingItem.id ? { ...item, ...payload } : item
          )
        );

        message.success("Menu updated successfully");
      } else {
        // CREATE
        const res = await axios.post(API_URL, payload);

        const newItem = {
          id: res.data?.data?.id || Date.now(),
          ...payload,
        };

        setMenuItems((prev) => [...prev, newItem]);

        message.success("Menu added successfully");
      }

      form.resetFields();
      setDrawerOpen(false);
      setEditingItem(null);
    } catch (error) {
      message.error("Failed to save menu");
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async(id) => {
    console.log(id);

    try {
      const req = await axios.delete(`${delete_api}?id=${id}&table=top_menu`);
      if(req.status){
        notify.success(req.message)
        setMenuItems((prev) => prev.filter((item) => item.id !== id));

      }else{
        notify.error(req.message)
      }

    } catch (error) {
      console.error(error);
      
    }
    
  };

  const tableColumns = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 70,
      align: "center",
      render: (id) => <Tag color="blue">{id}</Tag>,
    },
    {
      title: "Menu Name",
      dataIndex: "menu_list",
      key: "menu_list",
      render: (text) => (
        <Space>
          <AppstoreOutlined style={{ color: "#1677ff" }} />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Navigate To",
      dataIndex: "navigateTo",
      key: "navigateTo",
      render: (text) => (
        <Space>
          <LinkOutlined style={{ color: "#52c41a" }} />
          <Text code>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Created At",
      dataIndex: "added_on",
      key: "added_on",
      render: (date) => <Text type="secondary">{date}</Text>,
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      align: "center",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />

          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#f5f7fa" }}>
      <Card
        title="Manage Menus"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingItem(null);
              form.resetFields();
              setDrawerOpen(true);
            }}
          >
            Add Menu
          </Button>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 40 }} spin />} />
          </div>
        ) : menuItems.length === 0 ? (
          <Empty description="No menu items available." />
        ) : (
          <Table
            dataSource={menuItems}
            columns={tableColumns}
            rowKey="id"
            pagination={false}
            bordered
          />
        )}

        {/* Drawer */}
        <Drawer
          title={
            <Space>
              <PlusOutlined style={{ color: "#1677ff" }} />
              {editingItem ? "Edit Menu" : "Create Menu"}
            </Space>
          }
          placement="right"
          width={400}
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setEditingItem(null);
            form.resetFields();
          }}
          footer={
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setDrawerOpen(false);
                  setEditingItem(null);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>

              <Button
                type="primary"
                onClick={handleSave}
                loading={saving}
                icon={saving ? <LoadingOutlined /> : <PlusOutlined />}
              >
                {editingItem ? "Update" : "Save"}
              </Button>
            </Space>
          }
        >

          <Form form={form} layout="vertical">
            <Form.Item
              label="Menu Name"
              name="menuName"
              rules={[{ required: true, message: "Please enter menu name" }]}
            >
              <Input
                prefix={<AppstoreOutlined />}
                placeholder="Example: Products"
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="Navigate To"
              name="navigateTo"
              rules={[
                { required: true, message: "Please enter URL" },
                { pattern: /^\//, message: "URL should start with /" },
              ]}
            >
              <Input
                prefix={<LinkOutlined />}
                placeholder="/products"
                size="large"
              />
            </Form.Item>
          </Form>
        </Drawer>
      </Card>
    </div>
  );
};

export default TopMenu;